import { normalizeStr } from "@/utils/utils";
import { AdapterRecursoDaTelaComoArvore } from "@/permissoes/restricoes/restricoes.utils";
import type {
	IArvoreRestricoesResponse,
	IListarGruposUsuarioItem,
	IVerificaRecursosDaTelaItem,
	IVerificaRecursosDaTelaResponse,
} from "@/api/space/dtos/authenticator.dto";

/** Árvore de 1 módulo/1 recurso (ponte para reutilizar delta/resumo), ou `null` enquanto não há dados. */
export function montarArvoreRecurso(
	data: IVerificaRecursosDaTelaResponse | undefined,
): IArvoreRestricoesResponse | null {
	return data ? AdapterRecursoDaTelaComoArvore(data) : null;
}

/** Filtra grupos pela busca (descrição ou id). Mesma regra usada na sidebar. */
export function filtrarGruposPorBusca(grupos: IListarGruposUsuarioItem[], busca: string): IListarGruposUsuarioItem[] {
	const termo = normalizeStr(busca);
	return grupos.filter(
		(grupo) => !termo || normalizeStr(grupo.descricao).includes(termo) || String(grupo.id).includes(busca.trim()),
	);
}

/** Conta as permissões marcadas como ativas no estado de edição atual. */
export function contarPermissoesAtivas(
	data: IVerificaRecursosDaTelaResponse | undefined,
	estado: Map<string, boolean>,
): number {
	return data?.permissoes.reduce((total, permissao) => total + (estado.get(permissao.tag) ? 1 : 0), 0) ?? 0;
}

/** Permissão já resolvida para exibição: estado `ativo` do mapa de edição + flag de visibilidade pelo filtro. */
export type PermissaoVisivel = IVerificaRecursosDaTelaItem & { readonly visivel: boolean };

/** View-model derivada do recurso para o `ConteudoRestricoes` — concentra a "matemática" de busca/filtro. */
export type VisaoConteudoRecurso = {
	readonly recursoAtivo: boolean;
	readonly idsPermissoes: string[];
	readonly permissoesVisiveis: PermissaoVisivel[];
	readonly permissoesAtivas: number;
	readonly totalPermissoes: number;
	readonly todasPermissoesAtivas: boolean;
	readonly acessoTelaVisivel: boolean;
	readonly temConteudoVisivel: boolean;
};

type DerivarVisaoConteudoRecursoOptions = {
	readonly data: IVerificaRecursosDaTelaResponse;
	readonly estado: Map<string, boolean>;
	readonly buscaPermissao: string;
	readonly somenteAtivas: boolean;
	readonly isMesmoGrupo: boolean;
};

/**
 * Deriva a visão exibível do recurso (acesso à tela + permissões), aplicando a busca e o filtro
 * "somente ativas". O `ativo` vem do mapa de edição (reflete alterações não salvas). Quando o grupo é o
 * do próprio usuário (`isMesmoGrupo`), o acesso à tela não pode ser exibido/alterado.
 */
export function derivarVisaoConteudoRecurso({
	data,
	estado,
	buscaPermissao,
	somenteAtivas,
	isMesmoGrupo,
}: Readonly<DerivarVisaoConteudoRecursoOptions>): VisaoConteudoRecurso {
	const recursoAtivo = estado.get(data.recurso.tag) ?? false;
	const termo = normalizeStr(buscaPermissao);
	const recursoMatch =
		!termo || normalizeStr(data.recurso.descricao).includes(termo) || normalizeStr("Acesso à tela").includes(termo);

	const podeExibirAcessoTela = !isMesmoGrupo;
	const recursoVisivelPorFiltro = recursoMatch && (!somenteAtivas || recursoAtivo);

	const idsPermissoes = data.permissoes.map((permissao) => permissao.tag);

	const permissoes: PermissaoVisivel[] = data.permissoes.map((permissao) => {
		const ativo = estado.get(permissao.tag) ?? false;
		const correspondeBusca = recursoMatch || normalizeStr(permissao.descricao).includes(termo);
		return { ...permissao, ativo, visivel: correspondeBusca && (!somenteAtivas || ativo) };
	});

	const permissoesVisiveis = permissoes.filter((permissao) => permissao.visivel);
	const permissoesAtivas = permissoes.filter((permissao) => permissao.ativo).length;

	const totalPermissoes = data.permissoes.length;
	const todasPermissoesAtivas = totalPermissoes > 0 && permissoesAtivas === totalPermissoes;

	const acessoTelaVisivel = podeExibirAcessoTela && (recursoVisivelPorFiltro || permissoesVisiveis.length > 0);
	const temConteudoVisivel = acessoTelaVisivel || permissoesVisiveis.length > 0;

	return {
		recursoAtivo,
		idsPermissoes,
		permissoesVisiveis,
		permissoesAtivas,
		totalPermissoes,
		todasPermissoesAtivas,
		acessoTelaVisivel,
		temConteudoVisivel,
	};
}
